import React, { useCallback, useContext, useMemo } from 'react';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { Checkbox, IconButton, styled, TableCell, TableRow, Tooltip, Typography, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { DefaultExtensionType, defaultStyles, FileIcon } from 'react-file-icon';
import type { DocumentReferencesView } from 'api';
import { DialogContextValues } from 'common/types/dialogContext';
import { ExtraActionsMenu } from 'components';
import { ConfirmationDelete, ConfirmationDialog } from 'components/Dialogs';
import { DialogContext } from 'context';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import { InformationContextValue } from 'context/InformationDashboard409AContext/types';
import SuggestedDocumentReference from 'pages/Documents/components/SuggestedDocumentReference/SuggestedDocumentReference';
import FilesService from 'services/documents';
import { useDocuments } from 'services/hooks';
import { CurrentRequestedDocumentTableRowProps } from './types';
import { useDragRow } from '../hooks/useDragAndDropRow';
import useTableActionStyles from '../styles/useTableActionStyles';
import getFormattedDate from '../util/getFormattedDate';

const ConfirmationDeleteMessage = ({ message }: { message: string }) => (
  <ConfirmationDelete customMessage={message} itemName={message} />
);

const StyledTableCheckbox = styled(Checkbox)<{ variant?: 'root' | 'folder' | 'subFolder' }>(({ variant }) => ({
  // eslint-disable-next-line no-nested-ternary
  marginLeft: variant === 'root' ? 0 : variant === 'folder' ? '1rem' : '2rem',
}));

const CurrentRequestedDocumentTableRow: React.FC<CurrentRequestedDocumentTableRowProps> = (
  props: CurrentRequestedDocumentTableRowProps
) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const filesService = useMemo(() => new FilesService(), []);
  const {
    row,
    fileRequestMap,
    downloadDocument,
    setDraggedRow,
    setDraggedSelectedFiles,
    setSelectedFile,
    setSendFile,
    showRow,
    selectedFiles,
    setSelectedFiles,
    rowClass,
  } = props;
  const classes = useTableActionStyles();
  const { handleDragStart, handleDragEnd } = useDragRow(setDraggedRow, setDraggedSelectedFiles, selectedFiles);
  const { deleteFile } = useDocuments();
  const { showDialog } = useContext(DialogContext) as unknown as DialogContextValues;
  const { refreshDocuments, permissions } = useContext(
    InformationDashboard409AContext
  ) as unknown as InformationContextValue;

  const handleAcceptClassification = useCallback(async () => {
    try {
      await filesService.approveClassification(String(row.document_reference_id));
      enqueueSnackbar('Classification suggestion accepted', { variant: 'success' });
      refreshDocuments();
    } catch (error) {
      enqueueSnackbar('Failed to accept classification', { variant: 'error' });
    }
  }, [filesService, row.document_reference_id, enqueueSnackbar, refreshDocuments]);

  const handleDeclineClassification = useCallback(async () => {
    try {
      await filesService.declineClassification(String(row.document_reference_id));
      enqueueSnackbar('Classification suggestion dismissed', { variant: 'success' });
      refreshDocuments();
    } catch (error) {
      enqueueSnackbar('Failed to dismiss classification', { variant: 'error' });
    }
  }, [filesService, row.document_reference_id, enqueueSnackbar, refreshDocuments]);

  const handleDeleteFile = useCallback(async () => {
    if (row.md_documents) {
      await deleteFile(row.md_documents, row.file);
      refreshDocuments();
    }
  }, [deleteFile, refreshDocuments, row]);

  const showDeleteConfirmationDialog = useCallback(() => {
    showDialog({
      actions: [
        {
          callback: handleDeleteFile,
          label: 'Delete',
          type: 'danger',
          variant: 'contained',
        },
      ],
      content: ConfirmationDeleteMessage,
      contentProps: { message: `Are you sure you want to delete ${row.file.filename}?` },
      wrapper: ConfirmationDialog,
    });
  }, [handleDeleteFile, row.file.filename, showDialog]);

  const fileOptionsForExtraActionsMenu = useCallback(
    (rowParam: DocumentReferencesView) => [
      {
        label: 'Download',
        callback: () => {
          if (downloadDocument) {
            downloadDocument(rowParam);
          }
        },
        isActive: true,
      },
      {
        label: 'Edit Document',
        callback: () => {
          setSelectedFile(rowParam);
        },
        isActive: true,
      },
      {
        label: 'Send to another measurement date',
        callback: () => {
          setSendFile(rowParam);
        },
        isActive: true,
      },
      {
        label: 'Delete',
        callback: () => {
          showDeleteConfirmationDialog();
        },
        isActive: true,
      },
    ],
    [downloadDocument, setSelectedFile, setSendFile, showDeleteConfirmationDialog]
  );

  const handleSelectFile = useCallback(() => {
    if (!setSelectedFiles) {
      return;
    }
    setSelectedFiles(prev => {
      const index = prev.findIndex(file => file.file.id === row.file.id);
      if (index === -1) {
        return [...prev, row];
      }
      return prev.filter(file => file.file.id !== row.file.id);
    });
  }, [setSelectedFiles, row]);

  return (
    <TableRow
      draggable
      onDragStart={event => handleDragStart(event, row)}
      onDragEnd={handleDragEnd}
      hover
      className={!showRow ? classes.hiddenRow : ''}
      key={row.file.id}>
      <TableCell padding="checkbox">
        <StyledTableCheckbox
          checked={selectedFiles?.some(file => file?.file?.id === row?.file?.id)}
          onChange={handleSelectFile}
          variant={rowClass as 'root' | 'folder' | 'subFolder'}
        />
      </TableCell>
      <TableCell>
        <div
          style={{
            width: '1.5rem',
            height: '2rem',
            display: 'flex',
            alignItems: 'center',
          }}>
          <FileIcon extension={row.file.file_type} {...defaultStyles[row.file.file_type as DefaultExtensionType]} />
        </div>
      </TableCell>
      <TableCell>{row.file.filename}</TableCell>
      <TableCell>{row.file.uploaded_by?.full_name}</TableCell>
      <TableCell>{getFormattedDate(row.file.uploaded_date ?? null)}</TableCell>
      <TableCell>
        {(row as any).pending_auto_classification_approval ? (
          <SuggestedDocumentReference
            documentReference={row as any}
            onAccept={handleAcceptClassification}
            onDecline={handleDeclineClassification}
          />
        ) : (
          <ul>
            {row.file.id && fileRequestMap[row.file.id]?.length > 0 && (
              <li key={fileRequestMap[row.file.id][0].id}>
                <Typography variant="body2" style={{ color: (theme.palette.primary as unknown as Record<string, string>)[900] }}>
                  {fileRequestMap[row.file.id][0].name}
                  {fileRequestMap[row.file.id].length > 1 && (
                    <Tooltip
                      title={fileRequestMap[row.file.id]
                        .slice(1)
                        .map(request => request.name)
                        .join(', ')}>
                      <span> (...)</span>
                    </Tooltip>
                  )}
                </Typography>
              </li>
            )}
            {row.file.id && (row.referenced_in?.length ?? 0) > 0 && (
              <li key={row.referenced_in?.[0]?.id}>
                <Typography variant="body2" style={{ color: (theme.palette.secondary as unknown as Record<string, string>)[900] }}>
                  {row.referenced_in?.[0]?.reference_object_type}
                  {row.referenced_in && row.referenced_in.length > 1 && (
                    <Tooltip
                      title={row.referenced_in
                        .slice(1)
                        .map(reference => reference.reference_object_type)
                        .join(', ')}>
                      <span> (...)</span>
                    </Tooltip>
                  )}
                </Typography>
              </li>
            )}
          </ul>
        )}
      </TableCell>
      <TableCell>
        <div className={classes.actionMenu}>
          {permissions.canDownloadDocuments && (
            <IconButton size="small" onClick={() => downloadDocument(row)}>
              <CloudDownloadOutlinedIcon className={classes.icon} />
            </IconButton>
          )}
          {permissions?.canUseInfoRequestLedger && fileOptionsForExtraActionsMenu && (
            <ExtraActionsMenu options={fileOptionsForExtraActionsMenu(row)} dotsColor={theme.palette.grey[500]} />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default CurrentRequestedDocumentTableRow;
